import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_MS = 5000;
const POLL_WINDOW_MS = 60_000; // the STK prompt's lifetime

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
}

function maskPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  return `•••• ${digits.slice(-3)}`;
}

function endsIn(daysLeft) {
  if (daysLeft == null) return 'soon';
  if (daysLeft <= 0) return 'today';
  if (daysLeft === 1) return 'tomorrow';
  return `in ${daysLeft} days`;
}

/**
 * A one-off mobile-money plan runs out on a date; nothing renews it. This
 * banner says when, and renews it with one tap: a fresh STK push to the
 * number the last payment came from, then the same status poll the checkout
 * page uses. `renewal` is /subscription/status's `renewal` block.
 *
 * On the dashboard it appears only when `renewal.due`. Settings passes
 * `always` so the writer can renew early from the billing card.
 */
export default function RenewalBanner({ renewal, planName, email, onRenewed, always = false }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('idle'); // idle | pending | success | failed
  const [message, setMessage] = useState('');
  const [reference, setReference] = useState(null);
  const timerRef = useRef(null);
  const deadlineRef = useRef(0);
  // An in-flight poll resolving after unmount must not re-arm the timer or
  // touch state; the cleanup only clears the timer that is already pending.
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; clearTimeout(timerRef.current); }, []);

  if (!renewal) return null;
  // A banner that was due stays up to show the renewal landed, even though
  // the refreshed status is no longer due.
  if (!always && !renewal.due && phase !== 'success') return null;

  const stop = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const pollOnce = async (ref) => {
    try {
      const res = await authFetch(`${API}/payments/status/${ref}`, { credentials: 'include' });
      const data = await res.json();
      if (!aliveRef.current) return;
      if (!res.ok) throw new Error(data.detail || 'Status check failed');
      if (data.status === 'success') {
        stop();
        setPhase('success');
        toast.success(`Renewed. ${planName} carries on from ${formatDate(renewal.ends_at)}.`);
        onRenewed?.();
        return;
      }
      if (data.status === 'failed') {
        stop();
        setPhase('failed');
        setMessage(data.message || 'Payment was declined');
        return;
      }
    } catch (err) {
      console.error('Renewal poll error:', err);
    }
    if (!aliveRef.current) return;
    if (Date.now() >= deadlineRef.current) {
      stop();
      setPhase('failed');
      setMessage('No approval received. If you did approve, your plan will update shortly.');
      return;
    }
    timerRef.current = setTimeout(() => pollOnce(ref), POLL_MS);
  };

  const renew = async () => {
    if (!renewal.phone_number) {
      // No number on record to push to: fall back to the full checkout.
      navigate('/upgrade');
      return;
    }
    setPhase('pending');
    setMessage('');
    try {
      const res = await authFetch(`${API}/payments/mpesa/charge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan: renewal.plan,
          email,
          phone_number: renewal.phone_number,
          currency: 'KES',
        }),
      });
      const data = await res.json();
      if (!aliveRef.current) return;
      if (!res.ok) throw new Error(data.detail || 'Could not send the M-Pesa prompt');
      setReference(data.reference);
      deadlineRef.current = Date.now() + POLL_WINDOW_MS;
      timerRef.current = setTimeout(() => pollOnce(data.reference), POLL_MS);
    } catch (err) {
      setPhase('failed');
      setMessage(err.message || 'Could not start the renewal');
    }
  };

  const gold = '#E8A020';
  const urgent = renewal.due;

  return (
    <div
      className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-3 border-b"
      style={{
        background: urgent
          ? 'linear-gradient(90deg, rgba(196,122,10,0.12) 0%, rgba(232,160,32,0.18) 50%, rgba(196,122,10,0.12) 100%)'
          : 'transparent',
        borderColor: urgent ? 'rgba(232,160,32,0.3)' : 'rgba(255,255,255,0.06)',
      }}
      data-testid="renewal-banner"
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <Smartphone className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: gold }} />
        <div className="text-xs font-mono min-w-0" style={{ color: urgent ? gold : undefined }}>
          {phase === 'success' ? (
            <span className="flex items-center gap-1.5 text-green-400" data-testid="renewal-success">
              <CheckCircle className="w-3.5 h-3.5" /> Renewed. Your {planName} plan carries on.
            </span>
          ) : phase === 'pending' ? (
            <span className="flex items-center gap-1.5" data-testid="renewal-pending">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Check your phone for the M-Pesa prompt and enter your PIN.
              {reference && <span className="text-muted-foreground"> Ref: {reference}</span>}
            </span>
          ) : (
            <span data-testid="renewal-copy">
              Your {planName} plan ends {endsIn(renewal.days_left)} ({formatDate(renewal.ends_at)}).
              {' '}Paid by mobile money, so it does not renew on its own.
              {renewal.phone_number && <span className="text-muted-foreground"> One tap sends the prompt to {maskPhone(renewal.phone_number)}.</span>}
            </span>
          )}
          {phase === 'failed' && message && (
            <p className="text-muted-foreground mt-1" data-testid="renewal-message">{message}</p>
          )}
        </div>
      </div>
      {phase !== 'success' && (
        <button
          onClick={renew}
          disabled={phase === 'pending'}
          className="text-xs font-mono uppercase tracking-widest px-4 py-1.5 border shrink-0 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed self-start sm:self-auto"
          style={{ color: gold, borderColor: 'rgba(232,160,32,0.5)', background: 'rgba(232,160,32,0.1)' }}
          data-testid="renewal-cta"
        >
          {phase === 'failed' ? 'Try again' : 'Renew now'}
        </button>
      )}
    </div>
  );
}
